import { datadogRum } from "@datadog/browser-rum";

const MAX_STUCK_REPORTS = 3;
const MAX_TRANSIENT_REPORTS = 1;
// Stuck vs transient is only known after the re-check, so cap the snapshots themselves.
const MAX_SNAPSHOTS = 10;
const RECHECK_MS = 250;
const BROKEN_VAR = /--(?:anchor|available)-(?:width|height)\s*:\s*NaN/;
const POSITIONER_VAR = /(--(?:anchor|available)-(?:width|height))\s*:\s*([^;]*)/g;
const ANCHOR_ATTRS = ["aria-controls", "aria-owns", "aria-describedby"];
const HTML_OVERRIDABLE = ["scrollLeft", "scrollTop", "clientLeft", "getBoundingClientRect"];
const ANCHOR_ANCESTORS = 4;

type Probe = number | string | boolean | null;
type BrowserWindow = Window & typeof globalThis;

// JSON turns NaN/undefined into null, so encode anything that isn't a finite number as text.
const describe = (value: unknown): Probe => {
  if (typeof value === "number") return Number.isFinite(value) ? value : String(value);
  if (typeof value === "string" || typeof value === "boolean" || value === null) return value;
  return `<${typeof value}>`;
};

const probe = (read: () => unknown): Probe => {
  try {
    return describe(read());
  } catch (error) {
    return `<threw ${(error as Error)?.name ?? "error"}>`;
  }
};

const safely = <T extends object>(read: () => T): T | { snapshotError: string } => {
  try {
    return read();
  } catch (error) {
    return { snapshotError: String(error).slice(0, 200) };
  }
};

const isNative = (fn: unknown) =>
  typeof fn === "function" && /\{\s*\[native code\]\s*\}\s*$/.test(Function.prototype.toString.call(fn));

// Walks the element's real prototype chain, so a replaced window.Element can't hide a patched member.
const resolvedMember = (el: Element, key: string) => {
  for (let proto = Object.getPrototypeOf(el); proto; proto = Object.getPrototypeOf(proto)) {
    const descriptor = Object.getOwnPropertyDescriptor(proto, key);
    if (descriptor) return descriptor.get ?? descriptor.value;
  }
  return undefined;
};

const findAnchor = (doc: Document, positioner: Element) => {
  const ids = new Set([positioner, ...positioner.querySelectorAll("[id]")].map((el) => el.id).filter(Boolean));
  const candidates = [...doc.querySelectorAll(ANCHOR_ATTRS.map((attr) => `[${attr}]`).join(","))].filter(
    (el) => !positioner.contains(el),
  );
  for (const attr of ANCHOR_ATTRS) {
    const el = candidates.find((candidate) =>
      (candidate.getAttribute(attr) ?? "").split(/\s+/).some((id) => ids.has(id)),
    );
    if (el) return { el, attr };
  }
  // Tooltip triggers carry no aria link, but every open Base UI trigger is marked data-popup-open.
  const open = [...doc.querySelectorAll("[data-popup-open]")].filter((el) => !positioner.contains(el));
  return open.length ? { el: open[open.length - 1], attr: "data-popup-open" } : null;
};

// The aria-controls element is often not floating-ui's reference (a combobox anchors to its chips wrapper), so
// describe it and its nearest ancestors. Tags, data-slot and numbers only: no text, ids or labels.
const describeAnchorChain = (anchor: Element) => {
  const chain: Element[] = [];
  for (let el: Element | null = anchor; el && chain.length <= ANCHOR_ANCESTORS; el = el.parentElement) chain.push(el);
  return chain.map((el) => ({
    tag: el.tagName,
    slot: (el as HTMLElement).dataset?.slot ?? null,
    rectLeft: probe(() => el.getBoundingClientRect().left),
    rectWidth: probe(() => el.getBoundingClientRect().width),
    rectHeight: probe(() => el.getBoundingClientRect().height),
    ownOverrides: HTML_OVERRIDABLE.filter((key) => Object.prototype.hasOwnProperty.call(el, key)),
    nativeGetBoundingClientRect: isNative(resolvedMember(el, "getBoundingClientRect")),
    nativeScrollLeft: isNative(resolvedMember(el, "scrollLeft")),
  }));
};

// Origins only: script paths and query strings can carry tokens.
const scriptOrigins = (win: BrowserWindow) => {
  const origin = (url: string) => {
    try {
      const parsed = new URL(url, win.location.href);
      return `${parsed.protocol}//${parsed.host}`;
    } catch {
      return "<unparseable>";
    }
  };
  try {
    const urls = [
      ...[...win.document.querySelectorAll("script[src]")].map((el) => el.getAttribute("src") ?? ""),
      ...(win.performance?.getEntriesByType?.("resource") ?? [])
        .filter((entry) => (entry as PerformanceResourceTiming).initiatorType === "script")
        .map((entry) => entry.name),
    ];
    return [...new Set(urls.map(origin))];
  } catch {
    return ["<threw>"];
  }
};

// Inputs floating-ui reads to place a popup; the horizontal ones are where the NaN has shown up.
export const snapshotPopupPosition = (win: BrowserWindow, positioner: HTMLElement) => {
  const doc = win.document;
  const html = doc.documentElement;
  const anchor = findAnchor(doc, positioner);
  const popup = positioner.firstElementChild as HTMLElement | null;
  return {
    popupSlot: popup?.dataset.slot ?? null,
    popupRole: popup?.getAttribute("role") ?? null,
    side: positioner.dataset.side ?? null,
    align: positioner.dataset.align ?? null,
    vars: Object.fromEntries(
      [...(positioner.getAttribute("style") ?? "").matchAll(POSITIONER_VAR)].map(([, name, value]) => [
        name,
        value.trim(),
      ]),
    ),
    windowScrollX: probe(() => win.scrollX),
    windowScrollY: probe(() => win.scrollY),
    pageXOffset: probe(() => win.pageXOffset),
    htmlScrollLeft: probe(() => html.scrollLeft),
    htmlScrollTop: probe(() => html.scrollTop),
    bodyScrollLeft: probe(() => doc.body?.scrollLeft),
    htmlRectLeft: probe(() => html.getBoundingClientRect().left),
    htmlRectTop: probe(() => html.getBoundingClientRect().top),
    htmlClientLeft: probe(() => html.clientLeft),
    htmlClientWidth: probe(() => html.clientWidth),
    htmlOverflowX: probe(() => win.getComputedStyle(html).overflowX),
    direction: probe(() => win.getComputedStyle(html).direction),
    anchorAttr: anchor?.attr ?? null,
    anchorTag: anchor?.el.tagName ?? null,
    anchorRole: anchor?.el.getAttribute("role") ?? null,
    anchorRectLeft: probe(() => anchor?.el.getBoundingClientRect().left ?? null),
    anchorRectWidth: probe(() => anchor?.el.getBoundingClientRect().width ?? null),
    anchorRectTop: probe(() => anchor?.el.getBoundingClientRect().top ?? null),
    anchorChain: anchor ? describeAnchorChain(anchor.el) : null,
    visualViewportOffsetLeft: probe(() => win.visualViewport?.offsetLeft ?? null),
    visualViewportScale: probe(() => win.visualViewport?.scale ?? null),
    devicePixelRatio: probe(() => win.devicePixelRatio),
    htmlIsElement: probe(() => html instanceof win.Element),
    nativeElement: isNative(win.Element),
    nativeHTMLElement: isNative(win.HTMLElement),
    nativeNode: isNative(win.Node),
    nativeGetBoundingClientRect: isNative(resolvedMember(html, "getBoundingClientRect")),
    nativeScrollLeft: isNative(resolvedMember(html, "scrollLeft")),
    nativeGetComputedStyle: isNative(win.getComputedStyle),
    htmlOwnOverrides: HTML_OVERRIDABLE.filter((key) => Object.prototype.hasOwnProperty.call(html, key)),
    scrollGuardWrites: win.__scrollGlobalWrites?.length ?? 0,
    msSinceLoad: probe(() => Math.round(win.performance.now())),
    scriptOrigins: scriptOrigins(win),
  };
};

// Read after positioning settles: the first NaN write lands while Base UI still renders the popup fixed and hidden.
export const settledPopupPosition = (win: BrowserWindow, positioner: HTMLElement) => {
  const style = positioner.getAttribute("style") ?? "";
  const offsetParent = positioner.offsetParent;
  return {
    stuck: positioner.isConnected && BROKEN_VAR.test(style),
    connected: positioner.isConnected,
    position: probe(() => win.getComputedStyle(positioner).position),
    opacity: probe(() => win.getComputedStyle(positioner).opacity),
    hasTransform: /(?:^|;)\s*transform\s*:/.test(style),
    rectLeft: probe(() => positioner.getBoundingClientRect().left),
    rectTop: probe(() => positioner.getBoundingClientRect().top),
    offsetParentTag: offsetParent?.tagName ?? null,
    offsetParentRectLeft: probe(() => offsetParent?.getBoundingClientRect().left ?? null),
    offsetParentClientLeft: probe(() => offsetParent?.clientLeft ?? null),
    offsetParentScrollLeft: probe(() => offsetParent?.scrollLeft ?? null),
  };
};

// Reports popups that floating-ui placed with NaN, which renders them at the page's top-left or off-screen.
export const watchPopupPositions = (win: BrowserWindow = window) => {
  const seen = new WeakSet<Element>();
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const sent = { stuck: 0, transient: 0 };
  let snapshots = 0;

  const report = (positioner: HTMLElement, inputs: object) => {
    const settled = safely(() => settledPopupPosition(win, positioner));
    const stuck = "snapshotError" in settled || settled.stuck;
    // A popup that healed can break again after a scroll or resize; let the watcher see it again.
    if (!stuck) seen.delete(positioner);
    if (stuck ? sent.stuck >= MAX_STUCK_REPORTS : sent.transient >= MAX_TRANSIENT_REPORTS) return;
    sent[stuck ? "stuck" : "transient"] += 1;
    const error = new Error(stuck ? "Popup stuck with NaN position" : "Popup had a transient NaN position");
    error.name = "PopupPositionNaN";
    datadogRum.addError(error, { popup_position: { inputs, settled } });
    if (sent.stuck >= MAX_STUCK_REPORTS) stop();
  };

  const check = (el: Element) => {
    if (seen.has(el) || !BROKEN_VAR.test(el.getAttribute("style") ?? "")) return;
    seen.add(el);
    const inputs = safely(() => snapshotPopupPosition(win, el as HTMLElement));
    // Stop observing new episodes; the timers already pending still report.
    if (++snapshots >= MAX_SNAPSHOTS) observer.disconnect();
    const timer = setTimeout(() => {
      timers.delete(timer);
      report(el as HTMLElement, inputs);
    }, RECHECK_MS);
    timers.add(timer);
  };

  const observer = new win.MutationObserver((records) => records.forEach((record) => check(record.target as Element)));
  observer.observe(win.document.documentElement, { subtree: true, attributes: true, attributeFilter: ["style"] });

  function stop() {
    observer.disconnect();
    timers.forEach(clearTimeout);
    timers.clear();
  }
  return stop;
};
